import { useQuery } from '@tanstack/react-query';
import type { DateRange } from '../../src/api/queries';
import type { Source } from '../../src/source';
import { MAX_PAGE_SIZE } from '../../src/wire';
import { fetchIncidents, incidentsUrl } from './api';
import {
  CHRONICLE_MAX_EXTRA_DAYS,
  type ChronicleEntry,
  type ChronicleNote,
  chronicleDay,
  chronicleEntries,
  chronicleNotes,
  chronicleWindow,
  isDayBehind,
  isExhaustive,
  narrowToWindow,
} from './chronicle';
import { todayInTallinn } from './format';
import { useMeta, viewFilters } from './useStats';
import { DEFAULT_VIEW_STATE } from './useUrlState';

/**
 * The chronicle's data layer: `/api/meta` for the day, then one `/api/incidents` call per series.
 *
 * Both mounts — the landing rail and the site-wide Sheet — call this hook and share one cache
 * rather than one lifted state, so the second mount costs no request and the two cannot disagree.
 */

/** Everything the panel and the header's count badge need, in one object. */
export interface Chronicle {
  /** The covered day the panel states, or `null` while unknown or on an unread corpus. */
  day: string | null;
  /** The span actually shown, after the ≤2-day extension; `null` until `day` is known. */
  window: DateRange | null;
  entries: ChronicleEntry[];
  /** The window's item count, or `null` when the page cap makes it a lower bound. */
  count: number | null;
  /** Whether the stated day is older than yesterday — the "(viimane töödeldud päev)" condition. */
  isBehind: boolean;
  notes: ChronicleNote[];
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

/** One series over the widest span the extension may reach, fetched once: it already holds every
 * row the three candidate windows could show, so narrowing costs no second round trip. */
function useSeriesPage(source: Source, window: DateRange | null) {
  const state = { ...DEFAULT_VIEW_STATE, from: window?.from ?? '', to: window?.to ?? '' };
  const filters = {
    ...viewFilters('ALL', state, source),
    limit: MAX_PAGE_SIZE,
    order: 'date_desc' as const,
  };
  return useQuery({
    queryKey: [incidentsUrl(filters)],
    queryFn: () => fetchIncidents(filters),
    enabled: window !== null,
  });
}

export function useChronicle(now: () => number = Date.now): Chronicle {
  const meta = useMeta();
  const day = chronicleDay(meta.data);
  const widest = day === null ? null : chronicleWindow(day, CHRONICLE_MAX_EXTRA_DAYS);

  const digest = useSeriesPage('digest', widest);
  const traffic = useSeriesPage('traffic', widest);

  const digestPage = digest.data;
  const trafficPage = traffic.data;
  const narrowed =
    day === null || digestPage === undefined || trafficPage === undefined
      ? null
      : narrowToWindow(chronicleEntries(digestPage.items, trafficPage.items), day);

  const notes = narrowed === null ? [] : chronicleNotes(meta.data, narrowed.window);

  // Withheld rather than approximated: an unread series makes any count a lower bound, and a bare
  // "0" under the masthead — where the panel's notes are not visible — would read as a quiet night.
  const count =
    narrowed === null || digestPage === undefined || trafficPage === undefined || notes.length > 0
      ? null
      : isExhaustive(digestPage, narrowed.window.from) &&
          isExhaustive(trafficPage, narrowed.window.from)
        ? narrowed.entries.length
        : null;

  return {
    day,
    window: narrowed?.window ?? null,
    entries: narrowed?.entries ?? [],
    count,
    isBehind: day !== null && isDayBehind(day, todayInTallinn(now)),
    notes,
    // With no day there is nothing in flight: the two queries stay disabled, and a corpus nothing
    // has been read from is an answer rather than a wait.
    isPending: meta.isPending || (day !== null && (digest.isPending || traffic.isPending)),
    isError: meta.isError || digest.isError || traffic.isError,
    error: meta.error ?? digest.error ?? traffic.error,
  };
}
